import { CPLAT_API_ROUTES, usePost } from '@/api';
import AutoCloseOverlay from '@/components/atoms/AutoCloseOverlay';
import { Button } from '@/components/atoms/NewPcButton';
import Input from '@/components/V3/Input';
import { GA_EVENT_NAMES, GA_ROUTES } from '@/constants/ga';
import { ENTERED_BY_MARKET_NAME } from '@/constants/localstorage';
import { useLogEvent } from '@/hooks/use-log-event';
import { useMarketRegisterToken } from '@/hooks/use-market-register-token';
import { ROUTES } from '@/routes';
import { addHyphenPhoneNumber, removeHyphen } from '@/utils/phone';
import { cn } from '@/utils/tailwind';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { z } from 'zod';

const formSchema = z.object({
  phone: z.string(),
  verifyCode: z.string(),
});

type FormType = z.infer<typeof formSchema>;

export const SigninPhoneForm = () => {
  const TOTAL_SECONDS = 3 * 60; // 3분 = 180초
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const endTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const [smsToken, setSmsToken] = useState<string>();
  const [buttonLabel, setButtonLabel] = useState('인증번호 발송');
  const [_, setCplatToken] = useLocalStorage('cplatToken', '');
  const [showClosing, setShowClosing] = useState(false);
  const enteredByMarketName = localStorage.getItem(ENTERED_BY_MARKET_NAME);

  const navigate = useNavigate();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const autoClose = urlSearchParams.get('autoclose') === 'true';
  const redirectPath = urlSearchParams.get('redirect');
  const { onConnect } = useMarketRegisterToken();

  const { logEvent } = useLogEvent();

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
  } = useForm<FormType>({ resolver: zodResolver(formSchema) });

  const watchedPhone = watch('phone');
  const watchedVerifyCode = watch('verifyCode');

  const { mutate: onSendVerifyCode } = usePost(
    CPLAT_API_ROUTES.sendAuthenticationCode,
    undefined,
    undefined,
    true,
  );
  const { mutate: onSigninByPhone } = usePost(
    CPLAT_API_ROUTES.signinByPhone,
    undefined,
    undefined,
    true,
  );

  const { mutate: onVerifyCode } = usePost(
    CPLAT_API_ROUTES.verifyAuthenticationCode,
    undefined,
    undefined,
    true,
  );

  const { mutate: onCheck } = usePost(CPLAT_API_ROUTES.checkPhone, undefined, undefined, true);

  const update = useCallback(() => {
    if (!endTimeRef.current) return;

    const now = Date.now();
    const diff = Math.max(0, Math.ceil((endTimeRef.current - now) / 1000));

    setRemaining(diff);

    if (diff <= 0) {
      endTimeRef.current = null;
      cancelAnimationFrame(rafRef.current!);
      return;
    }

    rafRef.current = requestAnimationFrame(update);
  }, []);

  const restart = () => {
    cancelAnimationFrame(rafRef.current!);
    endTimeRef.current = Date.now() + TOTAL_SECONDS * 1000;
    setRemaining(TOTAL_SECONDS);
    rafRef.current = requestAnimationFrame(update);
  };

  const handleCheckPhone = () => {
    onCheck(
      { phoneNo: removeHyphen(watchedPhone) },
      {
        onSuccess: (response) => {
          const isRegistered = response.data.data[0].isRegistered;
          if (isRegistered) handleSendVerifyCode();
          else setError('phone', { message: '입력한 번호로 가입한 이력이 없습니다' });
        },
      },
    );
  };

  const handleSendVerifyCode = () => {
    onSendVerifyCode(
      { phoneNo: removeHyphen(watchedPhone) },
      {
        onSuccess: (response) => {
          clearErrors();
          setButtonLabel('재발송');
          restart();
          setSmsToken(response.data.data[0].smsToken);
        },
      },
    );
  };

  useEffect(() => {
    if (remaining === 0) setError('verifyCode', { message: '입력시간이 초과되었습니다' });
  }, [remaining]);

  const handlePhoneNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^\d-]/g, '');
    const digits = removeHyphen(value);

    if (digits.length > 11) return;

    const isnNotIncludePrefixNum = digits.length >= 3 && digits.substring(0, 3) !== '010';

    if (isnNotIncludePrefixNum) {
      setError('phone', {
        type: 'pattern',
        message: '휴대폰 번호는 "010-" 으로 시작해야합니다.',
      });
    } else {
      setError('phone', {});
    }

    value = addHyphenPhoneNumber(digits);

    setValue('phone', value);
  };

  useEffect(() => {
    if (watchedVerifyCode?.length === 6) {
      handleSubmit((values) =>
        onVerifyCode(
          { smsToken, smsCode: values.verifyCode },
          {
            onSuccess: (response) => {
              const verificationToken = response.data.data[0].verificationToken;
              onSigninByPhone(
                { verificationToken },
                {
                  onSuccess: (response) => {
                    const cplatToken = response.data.data[0].cplatToken;
                    // useLocalStorage 훅 + 직접 저장 (풀 리로드 시 확실히 읽히도록)
                    setCplatToken(cplatToken);
                    localStorage.setItem('cplatToken', JSON.stringify(cplatToken));
                    if (autoClose) {
                      setShowClosing(true);
                      setTimeout(() => {
                        if (window.opener || window.history.length <= 1) {
                          window.close();
                        }
                        window.location.replace(ROUTES.dashboard);
                      }, 1500);
                      return;
                    }
                    if (removeHyphen(values.phone) === '01093483900') {
                      navigate(ROUTES.admin);
                      return;
                    } else if (enteredByMarketName) {
                      onConnect(cplatToken);
                    } else {
                      localStorage.removeItem(ENTERED_BY_MARKET_NAME);
                      window.location.replace(redirectPath || ROUTES.root);
                    }
                  },
                },
              );
            },
            onError: (error) => {
              setError('verifyCode', {
                message: (error.response?.data as { message: string }).message,
              });
            },
          },
        ),
      )();
    }
  }, [watchedVerifyCode, handleSubmit]);

  return (
    <>
    {showClosing && <AutoCloseOverlay />}
    <div className="space-y-[88px]">
      <div className="flex flex-col gap-3">
        <div className="flex  gap-2 ">
          <Input
            placeholder="휴대폰 번호를 입력해 주세요"
            {...register('phone')}
            Error={{ isError: !!errors.phone?.message, errorMessage: errors.phone?.message }}
            onChange={handlePhoneNoChange}
          />
          <Button size="xsmall" disabled={!watchedPhone} onClick={handleCheckPhone}>
            {buttonLabel}
          </Button>
        </div>
        {buttonLabel === '재발송' && (
          <Input
            placeholder="인증번호를 입력해 주세요"
            InputProps={{
              endAdornment: (
                <span className={cn({ 'text-red-400': remaining === 0 })}>
                  {formatTime(remaining)}
                </span>
              ),
              endAdornmentStyle: 'text-blue-400 Body5M14 right-2',
            }}
            {...register('verifyCode')}
            Error={{
              isError: !!errors.verifyCode?.message,
              errorMessage: errors.verifyCode?.message,
            }}
            maxLength={6}
            autoComplete="off"
          />
        )}
      </div>
      <Button
        className="w-full"
        disabled={!watchedVerifyCode}
        onClick={handleSubmit((values) =>
          onVerifyCode(
            { smsToken, smsCode: values.verifyCode },
            {
              onSuccess: (response) => {
                const verificationToken = response.data.data[0].verificationToken;
                onSigninByPhone(
                  { verificationToken },
                  {
                    onSuccess: async (response) => {
                      const cplatToken = response.data.data[0].cplatToken;
                      setCplatToken(cplatToken);
                      logEvent({
                        eventName: GA_EVENT_NAMES.login.success.phone,
                        pageTitle: GA_ROUTES.login,
                      });
                      if (enteredByMarketName) onConnect(cplatToken);
                      else navigate(ROUTES.root);
                    },
                  },
                );
              },
              onError: (error) => {
                setError('verifyCode', {
                  message: (error.response?.data as { message: string }).message,
                });
              },
            },
          ),
        )}
      >
        로그인
      </Button>
    </div>
    </>
  );
};

const formatTime = (seconds: number) => {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};
